import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { X, Send, CheckCircle, Building, User, Phone, Mail, MessageSquare } from "lucide-react";
import { submitLead } from "@/lib/api";
import { toast } from "sonner";

export const LeadCaptureForm = ({ isOpen, onClose, problemTitle = "", verticalName = "" }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    company_size: "",
    message: "",
    problem_interest: problemTitle,
    vertical_interest: verticalName,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await submitLead(formData);
      setIsSuccess(true);
      toast.success("Thank you! Our team will contact you shortly.");
      
      // Reset and close after delay
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          company_size: "",
          message: "",
          problem_interest: problemTitle,
          vertical_interest: verticalName,
        });
        onClose();
      }, 2000);
    } catch (error) {
      toast.error("Failed to submit. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={onClose}
          data-testid="lead-capture-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0066CC] to-[#0052A3] px-6 py-5 text-white">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/10 transition-colors"
                data-testid="close-lead-form"
              >
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold">Schedule Your Free Demo</h2>
              <p className="text-blue-100 text-sm mt-1">
                Our experts will guide you through the perfect solution
              </p>
            </div>

            {/* Success State */}
            {isSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-8 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h3>
                <p className="text-slate-600">Our team will reach out to you within 24 hours.</p>
              </motion.div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Context Badge */}
                {(problemTitle || verticalName) && (
                  <div className="bg-blue-50 rounded-lg p-3 text-sm">
                    <p className="text-[#0066CC] font-medium">
                      Interested in: {problemTitle || verticalName}
                    </p>
                  </div>
                )}

                {/* Name & Email Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-slate-700 flex items-center gap-2">
                      <User className="w-3.5 h-3.5" /> Name *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Your full name"
                      required
                      className="focus-visible:ring-[#0066CC]"
                      data-testid="lead-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-slate-700 flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" /> Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="work@company.com"
                      required
                      className="focus-visible:ring-[#0066CC]"
                      data-testid="lead-email-input"
                    />
                  </div>
                </div>

                {/* Phone & Company Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-slate-700 flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5" /> Phone *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="+91 98765 43210"
                      required
                      className="focus-visible:ring-[#0066CC]"
                      data-testid="lead-phone-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="company" className="text-slate-700 flex items-center gap-2">
                      <Building className="w-3.5 h-3.5" /> Company
                    </Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => handleChange("company", e.target.value)}
                      placeholder="Company name"
                      className="focus-visible:ring-[#0066CC]"
                      data-testid="lead-company-input"
                    />
                  </div>
                </div>

                {/* Company Size */}
                <div className="space-y-1.5">
                  <Label htmlFor="company_size" className="text-slate-700">
                    Company Size
                  </Label>
                  <Select 
                    value={formData.company_size} 
                    onValueChange={(value) => handleChange("company_size", value)}
                  >
                    <SelectTrigger className="focus:ring-[#0066CC]" data-testid="lead-company-size-select">
                      <SelectValue placeholder="Select company size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-500">201-500 employees</SelectItem>
                      <SelectItem value="500+">500+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-slate-700 flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5" /> How can we help?
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    placeholder="Tell us about your requirements or challenges..."
                    rows={3}
                    className="resize-none focus-visible:ring-[#0066CC]"
                    data-testid="lead-message-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0066CC] hover:bg-[#0052A3] text-white h-12 text-base font-semibold gap-2"
                  data-testid="lead-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Request Free Demo
                    </>
                  )}
                </Button>

                {/* Privacy Note */}
                <p className="text-xs text-slate-500 text-center">
                  By submitting, you agree to our privacy policy. We&apos;ll never share your data.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
